import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import type { User as UserType } from '@/utils/auth/type';
import { isAdminOrManagerRole } from '@/constants/roles';
import { isMasterUser, canAccessWatiFeatures, canAccessAiSensyFeatures } from '@/utils/master';
import {
  User,
  Building2,
  Users,
  Shield,
  FormInput,
  Facebook,
  Globe,
  Phone,
  PhoneOutgoing,
  Users2,
  ChevronRight,
  Settings,
  ChevronDown,
  Workflow,
  Sparkles,
  Key,
  Database,
  MessageCircle,
  MessageSquare,
  Link as LinkIcon,
  Filter,
  History,
  Palette,
  Upload,
  Zap,
  Layers
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
  badge?: string;
  comingSoon?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
  icon?: React.ReactNode;
}

const SettingsSidebar = () => {
  const location = useLocation();
  const { userData: apiResponse } = useUser(); // Use shared context
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(['Security', 'User Management', 'System Configuration', 'Import', 'Integrations', 'Social Connections', 'Marketing Automation', 'WhatsApp Automation']));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userData, setUserData] = useState<UserType | null>(null);

  // Transform API data whenever it changes
  useEffect(() => {
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      const apiData = apiResponse.data.data;
      console.log('🔍 API Response from context:', apiResponse);

      // Transform API data to match UserType interface
      const transformedUser: UserType = {
        name: apiData.userName || '',
        phone: apiData.phone || '',
        email: apiData.userEmail || '',
        client_uuid: '', // Not available in API response
        client_name: apiData.client?.client_name || '',
        user_uuid: apiData.uuid || '',
        role_uuid: apiData.role?.uuid || '',
        role_name: apiData.role?.role_name || '',
        is_super_admin: apiData.role?.role_name?.toLowerCase().includes('admin') || false,
        role_permissions: [{
          add_contacts: true,
          add_event: true,
          add_leads: true,
          assign_leads: true,
          dashboard: true,
              delete_contacts: true,
              delete_event: true,
              delete_leads: true,
              edit_contacts: true,
              edit_event: true,
              edit_leads: true,
              export_leads: true,
              reports: true,
              trash: true,
              view_all_leads: true,
              view_contacts: true,
              view_event: true,
              view_leads: true
            }]
      };

      console.log('🔍 Transformed User Data:', transformedUser);
      setUserData(transformedUser);
    }
  }, [apiResponse]);

  // Navigation sections with icons and descriptions - Segregated by type
  const navSections: NavSection[] = [
    {
      title: 'Personal',
      icon: <User className="h-4 w-4" />,
      items: [
        {
          name: 'Profile',
          path: 'profile',
          icon: <User className="h-4 w-4" />,
          description: 'Manage your personal information'
        },
        {
          name: 'Accounts',
          path: 'accounts',
          icon: <Key className="h-4 w-4" />,
          description: 'Security and login settings'
        }
      ]
    },
    {
      title: 'Security',
      icon: <Shield className="h-4 w-4" />,
      items: [
        {
          name: 'Login History',
          path: 'login-history',
          icon: <History className="h-4 w-4" />,
          description: 'View your login activity and sessions'
        },
        {
          name: 'Session Management',
          path: 'session-management',
          icon: <Shield className="h-4 w-4" />,
          description: 'Manage your active sessions'
        }
      ]
    },
    {
      title: 'User Management',
      icon: <Users className="h-4 w-4" />,
      items: [
        {
          name: 'Roles',
          path: 'roles',
          icon: <Shield className="h-4 w-4" />,
          description: 'Define permissions and access',
          badge: 'Admin'
        },
        {
          name: 'Teams',
          path: 'teams',
          icon: <Building2 className="h-4 w-4" />,
          description: 'Organize team structure',
          badge: 'Admin'
        },
        {
          name: 'Users',
          path: 'users',
          icon: <Users className="h-4 w-4" />,
          description: 'Manage team members',
          badge: 'Admin'
        },
        {
          name: 'Round Robin',
          path: 'round-robin-groups',
          icon: <Users2 className="h-4 w-4" />,
          description: 'Configure lead distribution',
          badge: 'Admin'
        }
      ]
    },
    {
      title: 'System Configuration',
      icon: <Settings className="h-4 w-4" />,
      items: [
        {
          name: 'Client Information',
          path: 'client-info',
          icon: <Building2 className="h-4 w-4" />,
          description: 'Manage organization details',
          badge: 'Admin'
        },
        {
          name: 'Pipeline',
          path: 'pipeline',
          icon: <Workflow className="h-4 w-4" />,
          description: 'Lead pipeline stages',
          badge: 'Admin'
        },
        {
          name: 'Funnel Management',
          path: 'funnel-management',
          icon: <Filter className="h-4 w-4" />,
          description: 'Configure sales funnel stages',
          badge: 'Admin'
        },
        {
          name: 'Form Fields',
          path: 'form-fields',
          icon: <FormInput className="h-4 w-4" />,
          description: 'Customize lead forms',
          badge: 'Admin'
        },
        {
          name: 'Extended Lead Profile',
          path: 'lead-profile',
          icon: <Layers className="h-4 w-4" />,
          description: 'Sectioned Child Details, Communication & Status',
          badge: 'Admin'
        }
      ]
    },
    {
      title: 'Import',
      icon: <Upload className="h-4 w-4" />,
      items: [
        {
          name: 'Import Leads',
          path: 'import',
          icon: <Upload className="h-4 w-4" />,
          description: 'Import leads from CSV files',
          badge: 'New'
        },
        {
          name: 'Import Knowlarity',
          path: 'import-knowlarity',
          icon: <Phone className="h-4 w-4" />,
          description: 'Import Knowlarity call logs',
          badge: 'New'
        }
      ]
    },
    {
      title: 'Integrations',
      icon: <Database className="h-4 w-4" />,
      items: [
        {
          name: 'Website',
          path: 'website-integration',
          icon: <Globe className="h-4 w-4" />,
          description: 'Website form integration'
        },
        {
          name: 'Phone',
          path: 'phone-integration',
          icon: <Phone className="h-4 w-4" />,
          description: 'Phone system integration'
        },
        {
          name: 'Outbound Calling',
          path: 'outbound-calling',
          icon: <PhoneOutgoing className="h-4 w-4" />,
          description: 'Configure click-to-call providers',
          badge: 'New'
        },
        {
          name: 'Runo',
          path: 'runo',
          icon: <Zap className="h-4 w-4" />,
          description: 'CRM allocation and automation',
          badge: 'New'
        },
        {
          name: 'AiSensy',
          path: 'aisensy',
          icon: <MessageCircle className="h-4 w-4" />,
          description: 'WhatsApp automation via AiSensy',
          badge: 'New'
        },
        {
          name: 'WATI',
          path: 'wati',
          icon: <MessageSquare className="h-4 w-4" />,
          description: 'WhatsApp Business API via WATI',
          badge: 'New'
        }
      ]
    },
    {
      title: 'Webhooks',
      icon: <Database className="h-4 w-4" />,
      items: [
        {
          name: 'Webhooks',
          path: 'webhooks',
          icon: <Database className="h-4 w-4" />,
          description: 'Send lead data to external services',
          badge: 'New'
        }
      ]
    },
    {
      title: 'Social Connections',
      icon: <LinkIcon className="h-4 w-4" />,
      items: [
        {
          name: 'Facebook Pages',
          path: 'facebook-pages',
          icon: <Facebook className="h-4 w-4" />,
          description: 'Connect Facebook Pages for lead retrieval',
          badge: 'New'
        },
        {
          name: 'Facebook Pixel',
          path: 'facebook-pixel-integration',
          icon: <Facebook className="h-4 w-4" />,
          description: 'Track conversions with Facebook Pixel'
        }
      ]
    },
    {
      title: 'Marketing Automation',
      icon: <Workflow className="h-4 w-4" />,
      items: [
        {
          name: 'Email Branding',
          path: 'drip-campaigns/branding',
          icon: <Palette className="h-4 w-4" />,
          description: 'Customize email appearance',
          badge: 'New'
        },
        {
          name: 'Email Templates',
          path: 'drip-campaigns/templates',
          icon: <FormInput className="h-4 w-4" />,
          description: 'Manage reusable email templates',
          badge: 'New'
        },
        {
          name: 'WhatsApp Templates',
          path: 'aisensy',
          icon: <MessageCircle className="h-4 w-4" />,
          description: 'Manage WhatsApp templates (AiSensy)',
          badge: 'New'
        },
        {
          name: 'Drip Campaigns',
          path: 'drip-campaigns',
          icon: <Zap className="h-4 w-4" />,
          description: 'Email & WhatsApp sequences',
          badge: 'New'
        }
      ]
    },
    {
      title: 'WhatsApp Automation',
      icon: <MessageCircle className="h-4 w-4" />,
      items: [
        {
          name: 'Registration',
          path: 'whatsapp-automation/registration',
          icon: <MessageCircle className="h-4 w-4" />,
          description: 'Register for WhatsApp automation services',
          badge: 'New'
        }
      ]
    }
  ];

  // 🔐 Check if user has admin permissions based on role UUID
  // Admin items and sections are shown to:
  // 1. Super Administrator
  // 2. System Administrator
  // 3. Sales Director, Sales Manager, Support Manager
  const hasAdminAccess = () => {
    if (!userData || !userData.role_uuid) return false;
    return isAdminOrManagerRole(userData.role_uuid);
  };

  const showAdminItems = hasAdminAccess();
  const isMaster = isMasterUser();

  // Debug logging
  console.log('🔍 Settings Sidebar Debug:', {
    userData,
    role_uuid: userData?.role_uuid,
    role_name: userData?.role_name,
    showAdminItems,
    hasAdminAccess: hasAdminAccess(),
    isMaster
  });

  // Filter sections based on permissions
  // Auto-expand section containing the active page on navigation
  useEffect(() => {
    if (location.pathname.endsWith('/settings')) {
      return; // On settings home, don't auto-expand
    }

    const currentPath = location.pathname.replace('/settings/', '');

    // Find which section contains the active path
    const activeSectionTitle = navSections.find(section =>
      section.items.some(item => item.path === currentPath)
    )?.title;

    if (activeSectionTitle) {
      setCollapsedSections(prevCollapsed => {
        const newCollapsed = new Set(prevCollapsed);

        // Expand the section containing the active item
        newCollapsed.delete(activeSectionTitle);

        // Collapse ALL other sections
        navSections.forEach(section => {
          if (section.title !== activeSectionTitle) {
            newCollapsed.add(section.title);
          }
        });

        return newCollapsed;
      });
    }
  }, [location.pathname]);

  // For non-admin users, only show Personal and Security sections
  // For admin users (Super Administrator, System Administrator, Manager), show all sections
  const allowedSectionsForNonAdmin = ['Personal', 'Security'];

  const visibleSections = navSections
    .filter(section => {
      // If user is admin, show all sections
      if (showAdminItems) return true;

      // For non-admin users, only show Personal and Security
      return allowedSectionsForNonAdmin.includes(section.title);
    })
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        // WATI-related items (visible to master OR client_id 9)
        const watiItems = ['WATI', 'WhatsApp Templates'];
        if (watiItems.includes(item.name) && !canAccessWatiFeatures()) {
          return false;
        }

        // AiSensy - visible to master OR schools with WhatsApp enabled
        if (item.name === 'AiSensy' && !canAccessAiSensyFeatures()) {
          return false;
        }

        // Extended Lead Profile - configured by YellowSlate ops only, not client-side admins
        if (item.name === 'Extended Lead Profile' && !isMaster) {
          return false;
        }

        if (!item.badge) return true; // No badge = show to everyone
        if (item.badge === 'New') return true; // New features = show to everyone
        if (item.badge === 'Admin') return showAdminItems; // Admin items = check permissions
        return false; // Unknown badge type = hide
      })
    }))
    .filter(section => section.items.length > 0); // Remove empty sections

  // Helper function to check if a path is active
  const isPathActive = (itemPath: string) => {
    if (location.pathname.endsWith('/settings')) {
      return itemPath === 'profile';
    }

    // Remove '/settings/' prefix from current path
    const currentPath = location.pathname.replace('/settings/', '');

    // Exact match only - no nested path matching to prevent parent paths from being active
    return currentPath === itemPath;
  };

  // Helper function to generate the full href path
  const getFullPath = (path: string) => {
    return `/settings/${path}`;
  };

  // Toggle section collapse with auto-collapse behavior
  const toggleSection = (title: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(title)) {
      // Expanding this section - collapse ALL others
      const allSections = navSections.map(section => section.title);
      const sectionsToCollapse = allSections.filter(sectionTitle =>
        sectionTitle !== title
      );
      sectionsToCollapse.forEach(sectionTitle => newCollapsed.add(sectionTitle));
      newCollapsed.delete(title);
    } else {
      // Collapsing this section
      newCollapsed.add(title);
    }
    setCollapsedSections(newCollapsed);
  };

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-72'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="bg-brandColor p-2 rounded-lg">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage your workspace</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {visibleSections.map((section) => {
          const isSectionCollapsed = collapsedSections.has(section.title);
          const hasSectionActiveItem = section.items.some(item => isPathActive(item.path));
          
          return (
            <div key={section.title} className="space-y-1">
              {/* Section Header */}
              {!isCollapsed && (
                <button
                  onClick={() => toggleSection(section.title)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${hasSectionActiveItem ? 'text-brandColor dark:text-brandColor' : ''}`}
                >
                  <div className="flex items-center space-x-2">
                    {section.icon}
                    <span>{section.title}</span>
                  </div>
                  <ChevronDown className={`h-3 w-3 transition-transform ${isSectionCollapsed ? '-rotate-90' : ''}`} />
                </button>
              )}

              {/* Section Items */}
              <div className={`space-y-0.5 ${isSectionCollapsed && !isCollapsed ? 'hidden' : ''}`}>
                {section.items.map((item) => {
                  const isActive = isPathActive(item.path);
                  
                  if (item.comingSoon) {
                    return (
                      <div
                        key={item.path}
                        className="relative group cursor-not-allowed opacity-50"
                      >
                        <div className={`flex items-center px-3 py-2 rounded-lg text-gray-400 dark:text-gray-500 ${!isCollapsed ? 'space-x-3' : 'justify-center'}`}>
                          <div className="flex-shrink-0">{item.icon}</div>
                          {!isCollapsed && (
                            <div className="flex-1 flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium">{item.name}</span>
                                {item.description && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{item.description}</p>
                                )}
                              </div>
                              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded">Soon</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.path}
                      to={getFullPath(item.path)}
                      className={`
                        relative group flex items-center px-3 py-2 rounded-lg transition-all duration-200
                        ${isActive
                          ? 'bg-gray-50 dark:bg-gray-800 text-brandColor dark:text-brandColor border-l-4 border-brandColor'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }
                        ${!isCollapsed ? 'space-x-3' : 'justify-center'}
                      `}
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 ${isActive ? 'text-brandColor dark:text-brandColor' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                        {item.icon}
                      </div>

                      {/* Content */}
                      {!isCollapsed && (
                        <div className="flex-1 flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium">{item.name}</span>
                            {item.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                            )}
                          </div>
                          {item.badge && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.badge === 'Admin'
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                : item.badge === 'New'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                          {item.name}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/docs"
            className="flex items-center space-x-2 text-xs text-brandColor dark:text-brandColor hover:text-brand-hover dark:hover:text-brand-hover transition-colors"
          >
            <Sparkles className="h-3 w-3" />
            <span>Need help? Check our docs</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default SettingsSidebar;